import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  FiPlus, FiUsers, FiShield, FiEdit2, FiTrash2, FiX, FiSave,
  FiCheck, FiMail, FiUser, FiAlertCircle, FiLock, FiEye, FiEyeOff, FiPhone, FiTool,
  FiChevronRight, FiChevronDown, FiKey
} from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, orderBy, setDoc, arrayUnion } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import usePagePermissions from '../../hooks/usePagePermissions';
import useEscapeClose from '../../hooks/useEscapeClose';
import { reportsOfHead, REPORT_ITEMS_KEY } from '../../utils/reportRights';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeaderText = styled.div``;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 8px 0;
`;

const PageDescription = styled.p`
  font-size: 14px;
  color: ${colors.text.secondary};
  margin: 0;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 24px;
  background: ${colors.neutral[100]};
  padding: 4px;
  border-radius: ${colors.borderRadius.lg};
  width: fit-content;
`;

const Tab = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$active ? colors.primary.main : colors.text.secondary};
  box-shadow: ${props => props.$active ? colors.shadows.sm : 'none'};

  &:hover {
    color: ${props => props.$active ? colors.primary.main : colors.text.primary};
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: ${colors.primary.main};
  color: white;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${colors.primary.dark};
    transform: translateY(-1px);
  }
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const Alert = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  margin-bottom: 20px;
  background: ${props => props.$type === 'success' ? colors.success.main + '15' : colors.error.main + '15'};
  color: ${props => props.$type === 'success' ? colors.success.main : colors.error.main};
  border: 1px solid ${props => props.$type === 'success' ? colors.success.main + '30' : colors.error.main + '30'};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 14px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[200]};
  transition: background 0.2s;

  &:hover {
    background: ${colors.neutral[50]};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Td = styled.td`
  padding: 16px;
  font-size: 14px;
  color: ${colors.text.primary};
  vertical-align: middle;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: ${colors.borderRadius.sm};
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.$active ? colors.success.main + '20' : colors.neutral[200]};
  color: ${props => props.$active ? colors.success.main : colors.text.secondary};
`;

const RoleBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: ${colors.borderRadius.sm};
  font-size: 12px;
  font-weight: 500;
  background: ${colors.primary.main}20;
  color: ${colors.primary.main};
`;

const SystemBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: ${colors.borderRadius.sm};
  font-size: 10px;
  font-weight: 600;
  background: ${colors.warning.main}20;
  color: ${colors.warning.main};
  text-transform: uppercase;
  margin-left: 8px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button`
  padding: 8px;
  background: ${props => props.$danger ? colors.error.main + '15' : colors.neutral[100]};
  color: ${props => props.$danger ? colors.error.main : colors.text.secondary};
  border: none;
  border-radius: ${colors.borderRadius.sm};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.$danger ? colors.error.main + '25' : colors.neutral[200]};
    color: ${props => props.$danger ? colors.error.main : colors.text.primary};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  color: ${colors.neutral[300]};
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin: 0 0 8px 0;
`;

const EmptySubtext = styled.p`
  font-size: 14px;
  margin: 0;
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  width: 100%;
  max-width: ${props => props.$wide ? '800px' : '500px'};
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${colors.shadows.lg};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  color: ${colors.text.secondary};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${colors.borderRadius.sm};
  transition: all 0.2s;

  &:hover {
    background: ${colors.neutral[100]};
    color: ${colors.text.primary};
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid ${colors.neutral[200]};
  background: ${colors.neutral[50]};
`;

const FormGroup = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: ${colors.text.primary};
  margin-bottom: 6px;

  span {
    color: ${colors.error.main};
    margin-left: 2px;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }

  &:disabled {
    background: ${colors.neutral[100]};
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background: white;
  color: ${colors.text.primary};
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: ${colors.primary.main};
  color: white;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${colors.primary.dark};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PermissionsGrid = styled.div`
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  overflow: hidden;
`;

const PermissionRow = styled.div`
  display: grid;
  grid-template-columns: 200px repeat(4, 1fr);
  border-bottom: 1px solid ${colors.neutral[200]};

  &:last-child {
    border-bottom: none;
  }
`;

const PermissionHeader = styled(PermissionRow)`
  background: ${colors.neutral[50]};
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
`;

const PermissionCell = styled.div`
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: ${props => props.$center ? 'center' : 'flex-start'};
  border-right: 1px solid ${colors.neutral[200]};

  &:last-child {
    border-right: none;
  }
`;

const ModuleName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.text.primary};
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: ${colors.primary.main};
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${colors.primary.main}20;
  color: ${colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 600;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const UserName = styled.span`
  font-weight: 500;
  color: ${colors.text.primary};
`;

const UserEmail = styled.span`
  font-size: 12px;
  color: ${colors.text.secondary};
`;

const InviteCodeBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: ${colors.neutral[100]};
  border-radius: ${colors.borderRadius.md};
  margin-top: 8px;
`;

const InviteCode = styled.code`
  flex: 1;
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 14px;
  color: ${colors.primary.main};
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${colors.primary.main};
  color: white;
  border: none;
  border-radius: ${colors.borderRadius.sm};
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: ${colors.primary.dark};
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const PasswordInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  color: ${colors.text.secondary};
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;

  &:hover {
    color: ${colors.primary.main};
  }
`;

const HelpText = styled.span`
  font-size: 11px;
  color: ${colors.text.secondary};
  margin-top: 4px;
`;

const BranchCheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  max-height: 200px;
  overflow-y: auto;
`;

const BranchCheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${colors.text.primary};
  cursor: pointer;

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: ${colors.primary.main};
  }
`;

const BranchCode = styled.span`
  font-size: 12px;
  color: ${colors.text.secondary};
`;

// Permission categories for the tabbed role editor
const permissionCategories = [
  {
    key: 'masterData', name: 'Master Data', addSplit: true,
    actions: ['view', 'edit', 'delete'],
    modules: [
      { key: 'chartOfAccounts', name: 'Chart of Accounts' },
      { key: 'customers', name: 'Customers' },
      { key: 'vendors', name: 'Vendors' },
      { key: 'banks', name: 'Banks' },
      { key: 'items', name: 'Items / Products' },
      { key: 'bulkItemUpdate', name: 'Bulk Item Update', noAddSplit: true },
      { key: 'locations', name: 'Inventory Locations' },
      { key: 'employees', name: 'Employees' },
      { key: 'regions', name: 'Regions' },
      { key: 'jobs', name: 'Jobs / Projects' },
      { key: 'openingBalances', name: 'Opening Balances', noAddSplit: true },
      { key: 'importChargeTemplates', name: 'Import Charge Templates' },
      { key: 'units', name: 'Units of Measure' },
    ]
  },
  {
    key: 'transactions', name: 'Transactions', addSplit: true,
    actions: ['view', 'edit', 'delete', 'post'],
    modules: [
      { key: 'purchaseOrder', name: 'Purchase Order', extraActions: ['cancel'] },
      { key: 'grn', name: 'GRN' },
      { key: 'purchaseInvoice', name: 'Purchase Invoice', extraActions: ['cancel'] },
      { key: 'purchaseReturn', name: 'Purchase Return' },
      { key: 'quotation', name: 'Quotation' },
      { key: 'saleOrder', name: 'Sale Order', extraActions: ['cancel'] },
      { key: 'deliveryChallan', name: 'Delivery Challan', extraActions: ['cancel'] },
      { key: 'saleInvoice', name: 'Sale Invoice', extraActions: ['cancel'] },
      { key: 'saleReturn', name: 'Sale Return' },
      { key: 'nonTaxSaleInvoice', name: 'Non-Tax Sale Invoice', extraActions: ['cancel'] },
      { key: 'nonTaxSaleReturn', name: 'Non-Tax Sale Return' },
      { key: 'nonTaxPurchaseInvoice', name: 'Non-Tax Purchase Invoice', extraActions: ['cancel'] },
      { key: 'nonTaxPurchaseReturn', name: 'Non-Tax Purchase Return' },
      { key: 'importPurchaseInvoice', name: 'Import Purchase Invoice' },
      { key: 'cashReceipt', name: 'Cash Receipt' },
      { key: 'bankReceipt', name: 'Bank Receipt' },
      { key: 'cashPayment', name: 'Cash Payment' },
      { key: 'bankPayment', name: 'Bank Payment' },
      { key: 'journalVoucher', name: 'Journal Voucher' },
      { key: 'cashBook', name: 'Cash Book' },
      { key: 'addStock', name: 'Add Stock' },
      { key: 'reduceStock', name: 'Reduce Stock' },
      { key: 'stockTransfer', name: 'Stock Transfer' },
      { key: 'posCounterSale', name: 'POS Counter Sale', noAddSplit: true },
      { key: 'posPriceEdit', name: 'POS — Edit Line Price', noAddSplit: true },
    ]
  },
  {
    key: 'store', name: 'Store', addSplit: true,
    actions: ['view', 'edit', 'delete', 'post'],
    modules: [
      { key: 'purchaseRequisition', name: 'Purchase Requisition' },
      { key: 'inwardGatePass', name: 'Inward Gate Pass' },
      { key: 'outwardGatePass', name: 'Outward Gate Pass' },
      { key: 'billOfMaterials', name: 'Bill of Materials' },
      { key: 'productionOrder', name: 'Production Order' },
      { key: 'materialIssue', name: 'Material Issue to Production' },
      { key: 'productionReceipt', name: 'Production Receipt' },
    ]
  },
  {
    key: 'salary', name: 'Salary',
    actions: ['view', 'edit', 'delete', 'post'],
    modules: [
      { key: 'salaryStaff', name: 'Salary Staff' },
      { key: 'advance', name: 'Salary Advance' },
      { key: 'loan', name: 'Staff Loan' },
      { key: 'loanRecovery', name: 'Loan Recovery (cash / bank)' },
      { key: 'payrollRun', name: 'Payroll Run' },
      { key: 'finalSettlement', name: 'Final Settlement' },
      { key: 'attendance', name: 'Attendance (machine logs)' },
      { key: 'attendanceSettings', name: 'Attendance Settings & Devices' },
    ]
  },
  {
    key: 'fixedAssets', name: 'Fixed Assets',
    actions: ['view', 'edit', 'delete', 'post'],
    modules: [
      { key: 'assets', name: 'Asset Register & Categories' },
      { key: 'depreciation', name: 'Depreciation Run' },
      { key: 'disposal', name: 'Asset Disposal' },
      { key: 'appreciation', name: 'Asset Appreciation' },
      { key: 'reports', name: 'Asset Reports' },
    ]
  },
  {
    key: 'whatsapp', name: 'WhatsApp',
    actions: ['view', 'edit'],
    modules: [
      { key: 'connection', name: 'Connection (scan QR / disconnect)' },
      { key: 'send', name: 'Send Documents via WhatsApp' },
      { key: 'templates', name: 'Message Templates' },
      { key: 'logs', name: 'Send Logs' },
      { key: 'settings', name: 'WhatsApp Settings' },
    ]
  },
  {
    key: 'reports', name: 'Reports',
    actions: ['toggle'],
    modules: [
      { key: 'purchaseReports', name: 'Purchase Reports' },
      { key: 'saleReports', name: 'Sale Reports' },
      { key: 'inventoryReports', name: 'Inventory Reports' },
      { key: 'financialReports', name: 'Financial Reports' },
      { key: 'posReports', name: 'POS Reports' },
      { key: 'profitReports', name: 'Profit Reports' },
      { key: 'intelligenceReports', name: 'Intelligence Reports' },
      { key: 'salaryReports', name: 'Salary Reports' },
      { key: 'jobReports', name: 'Job Reports' },
      { key: 'productionReports', name: 'Production Reports' },
    ]
  },
  {
    key: 'settings', name: 'Settings',
    actions: ['view', 'edit'],
    modules: [
      { key: 'myCompany', name: 'My Company' },
      { key: 'usersRoles', name: 'Users & Roles' },
      { key: 'financialYear', name: 'Financial Year' },
      { key: 'documentSettings', name: 'Document Settings' },
      { key: 'printFormats', name: 'Print Formats' },
      { key: 'branchSetup', name: 'Branch Setup' },
      { key: 'posCounterSetup', name: 'POS Counters' },
      { key: 'inventoryMovement', name: 'Inventory Movement' },
    ]
  },
  {
    // 2026-10-27: Ask AI answers from ALL company data (balances, invoices,
    // ledger, profit) whatever the user's other rights — so it has its own tick.
    // Owner / admin always have it. Roles saved before this have it OFF
    // (offWhenMissing), and the server function checks the same tick.
    key: 'ai', name: 'AI Assistant',
    actions: ['toggle'],
    offWhenMissing: true,
    modules: [
      { key: 'askAI', name: 'Ask AI (can see all company figures)' },
    ]
  },
  {
    // Dashboard widgets — toggle per role so admins can hide finance/sales
    // metrics from cashiers, sales floor staff, etc. Module keys MUST match
    // the widget ids referenced in Dashboard.js (hasPermission lookups).
    key: 'dashboard', name: 'Dashboard Widgets',
    actions: ['toggle'],
    // Corrected 2026-08-06. These entries had drifted out of sync with the
    // actual dashboard: three were MISLABELLED (the July rebuild reused the old
    // widgets' permission keys, so "Cash Flow" was really controlling Sales vs
    // Recovery) and three controlled NOTHING at all because their widgets were
    // deleted. Admins were making access decisions on labels that lied.
    // Old keys are still honoured at read time — see LEGACY_WIDGET_PERM_KEY in
    // src/pages/Dashboard.js — so a role that deliberately hid one of these
    // keeps hiding it until the role is next saved.
    modules: [
      { key: 'executiveSummary', name: 'Executive Summary' },
      { key: 'kpiCards',         name: 'KPI Cards' },
      { key: 'salesOverview',    name: 'Sales Overview' },      // was mislabelled "Sales Intelligence"
      { key: 'salesRecovery',    name: 'Sales vs Recovery' },   // was mislabelled "Cash Flow"
      { key: 'salesExpense',     name: 'Sales vs Expense' },    // was mislabelled "Financial Health"
      { key: 'moneyPosition',    name: 'Money Position' },
      { key: 'receivablePayable', name: 'Receivable vs Payable' },
      { key: 'activityFeed',     name: 'Activity Feed' },
      { key: 'whatsNew',         name: "What's New" },
    ]
  }
];

// Some documents have a tick beyond their category's columns — Cancel
// (2026-11-04), only on documents that can be cancelled. actionsOf = the ticks a
// module really has; columnsOf = the columns its category shows. A role saved
// before the Cancel tick existed simply has it OFF.
//
// Add tick (2026-11-04): on data-entry categories (addSplit) a module shows
// "Add" before "Edit". Stored the same way as before — 'edit' stays the write
// right — plus addOnly: true when only Add is ticked. So Add = edit, and
// Edit = edit && !addOnly. Roles saved before this have no addOnly, so both
// ticks show exactly as their old Edit tick.
const hasAddSplit = (cat, mod) => !!cat.addSplit && !(mod && mod.noAddSplit);
const withAdd = (acts) => acts.flatMap(a => (a === 'edit' ? ['add', 'edit'] : [a]));
const actionsOf = (cat, mod) => {
  const base = hasAddSplit(cat, mod) ? withAdd(cat.actions) : cat.actions;
  return mod && Array.isArray(mod.extraActions) ? [...base, ...mod.extraActions] : base;
};
// What is actually saved on the role ('add' lives in edit + addOnly).
const storedActionsOf = (cat, mod) => actionsOf(cat, mod).filter(a => a !== 'add');
const columnsOf = (cat) => {
  const base = cat.addSplit ? withAdd(cat.actions) : cat.actions;
  return [...base, ...[...new Set(cat.modules.flatMap(m => m.extraActions || []))].filter(a => !base.includes(a))];
};
// Is this tick on for the module's saved rights?
const permOn = (cat, mod, mp, action) => {
  if (!mp) return false;
  if (hasAddSplit(cat, mod)) {
    if (action === 'add') return mp.edit === true;
    if (action === 'edit') return mp.edit === true && mp.addOnly !== true;
  }
  return mp[action] === true;
};

// Build empty default permissions (all false)
const buildDefaultPermissions = () => {
  const p = {};
  permissionCategories.forEach(cat => {
    p[cat.key] = {};
    cat.modules.forEach(mod => {
      if (cat.actions[0] === 'toggle') {
        p[cat.key][mod.key] = false;
      } else {
        p[cat.key][mod.key] = {};
        storedActionsOf(cat, mod).forEach(a => { p[cat.key][mod.key][a] = false; });
      }
    });
  });
  return p;
};

// Build permissions starting from all-false, applying only the true overrides.
// Exception: dashboard widgets default to ALL VISIBLE for every role — admins
// can still revoke per role via the UI, but the out-of-the-box experience is
// that every user sees the company dashboard when they log in.
const buildPermissions = (overrides = {}) => {
  const base = buildDefaultPermissions();
  // Grant all dashboard widgets by default
  if (base.dashboard) {
    Object.keys(base.dashboard).forEach(k => { base.dashboard[k] = true; });
  }
  Object.entries(overrides).forEach(([cat, mods]) => {
    if (!base[cat]) return;
    Object.entries(mods).forEach(([mod, val]) => {
      if (base[cat][mod] === undefined) return;
      if (typeof val === 'boolean') {
        base[cat][mod] = val;
      } else {
        base[cat][mod] = { ...base[cat][mod], ...val };
      }
    });
  });
  return base;
};

// Admin: everything true
const buildAllTruePermissions = () => {
  const p = {};
  permissionCategories.forEach(cat => {
    p[cat.key] = {};
    cat.modules.forEach(mod => {
      if (cat.actions[0] === 'toggle') {
        p[cat.key][mod.key] = true;
      } else {
        p[cat.key][mod.key] = {};
        storedActionsOf(cat, mod).forEach(a => { p[cat.key][mod.key][a] = true; });
      }
    });
  });
  return p;
};

// Migrate old flat permissions structure to new nested structure.
// Also forward-fills any category that exists in the matrix today but is missing
// on the stored doc — keeps existing roles working when we add a new category
// (e.g. `dashboard`) without an explicit migration.
const ensureNewCategories = (perms) => {
  if (!perms || typeof perms !== 'object') return perms;
  permissionCategories.forEach(cat => {
    if (perms[cat.key]) return; // already present — admin can edit through the UI
    // Category was added after this role was saved → grant everything by default
    // so legacy users don't suddenly lose access. Admin can revoke per role later.
    // Exception: categories marked offWhenMissing (Ask AI) start OFF.
    perms[cat.key] = {};
    cat.modules.forEach(mod => {
      if (cat.actions[0] === 'toggle') {
        perms[cat.key][mod.key] = !cat.offWhenMissing;
      } else {
        perms[cat.key][mod.key] = {};
        cat.actions.forEach(a => { perms[cat.key][mod.key][a] = true; });
      }
    });
  });
  return perms;
};

const migratePermissions = (oldPerms) => {
  if (!oldPerms) return buildDefaultPermissions();
  if (oldPerms.masterData || oldPerms.transactions) return ensureNewCategories({ ...oldPerms });

  const p = buildDefaultPermissions();
  const m = (v) => ({ view: v?.view || false, edit: v?.create || v?.edit || false, delete: v?.delete || false });
  const ms = (v) => ({ view: v?.view || false, edit: v?.create || v?.edit || false });

  if (oldPerms.chartOfAccounts) p.masterData.chartOfAccounts = m(oldPerms.chartOfAccounts);
  if (oldPerms.chartOfInventory) p.masterData.items = m(oldPerms.chartOfInventory);
  if (oldPerms.customers) p.masterData.customers = m(oldPerms.customers);
  if (oldPerms.vendors) p.masterData.vendors = m(oldPerms.vendors);
  if (oldPerms.banks) p.masterData.banks = m(oldPerms.banks);
  if (oldPerms.employees) p.masterData.employees = m(oldPerms.employees);

  if (oldPerms.purchases) {
    const a = m(oldPerms.purchases);
    ['purchaseOrder', 'grn', 'purchaseInvoice', 'purchaseReturn'].forEach(k => { p.transactions[k] = { ...a }; });
  }
  if (oldPerms.sales) {
    const a = m(oldPerms.sales);
    ['quotation', 'saleOrder', 'deliveryChallan', 'saleInvoice', 'saleReturn'].forEach(k => { p.transactions[k] = { ...a }; });
  }
  if (oldPerms.receipts) {
    const a = m(oldPerms.receipts);
    ['cashReceipt', 'bankReceipt'].forEach(k => { p.transactions[k] = { ...a }; });
  }
  if (oldPerms.payments) {
    const a = m(oldPerms.payments);
    ['cashPayment', 'bankPayment'].forEach(k => { p.transactions[k] = { ...a }; });
  }
  if (oldPerms.journal) p.transactions.journalVoucher = m(oldPerms.journal);
  if (oldPerms.store) {
    const a = m(oldPerms.store);
    Object.keys(p.store).forEach(k => { p.store[k] = { ...a }; });
  }
  if (oldPerms.reports?.view) Object.keys(p.reports).forEach(k => { p.reports[k] = true; });
  if (oldPerms.settings) {
    const a = ms(oldPerms.settings);
    Object.keys(p.settings).forEach(k => { p.settings[k] = { ...a }; });
  }
  // Dashboard widgets: default to all visible for legacy roles being migrated.
  if (p.dashboard) {
    Object.keys(p.dashboard).forEach(k => { p.dashboard[k] = true; });
  }
  return p;
};

// Default roles to seed when no roles exist
// Note: Super Admin is a PLATFORM level role (application owner), not a company role
const defaultRoles = [
  {
    name: 'Admin',
    description: 'Full access to all company modules and user management',
    isSystemRole: true,
    permissions: buildAllTruePermissions()
  },
  {
    name: 'Accountant',
    description: 'Access to financial and accounting modules',
    isSystemRole: false,
    permissions: buildPermissions({
      masterData: {
        chartOfAccounts: { view: true, edit: true }, customers: { view: true }, vendors: { view: true },
        banks: { view: true, edit: true }, items: { view: true }, locations: { view: true },
        employees: { view: true }, regions: { view: true }, jobs: { view: true },
        openingBalances: { view: true, edit: true }
      },
      transactions: {
        purchaseOrder: { view: true }, grn: { view: true },
        purchaseInvoice: { view: true, edit: true }, purchaseReturn: { view: true, edit: true },
        importPurchaseInvoice: { view: true, edit: true },
        quotation: { view: true }, saleOrder: { view: true }, deliveryChallan: { view: true },
        saleInvoice: { view: true, edit: true }, saleReturn: { view: true, edit: true },
        cashReceipt: { view: true, edit: true }, bankReceipt: { view: true, edit: true },
        cashPayment: { view: true, edit: true }, bankPayment: { view: true, edit: true },
        journalVoucher: { view: true, edit: true }, cashBook: { view: true, edit: true },
        addStock: { view: true }, reduceStock: { view: true }, stockTransfer: { view: true }
      },
      store: {
        purchaseRequisition: { view: true }, inwardGatePass: { view: true },
        outwardGatePass: { view: true }, billOfMaterials: { view: true }, productionOrder: { view: true },
        materialIssue: { view: true }, productionReceipt: { view: true }
      },
      fixedAssets: {
        assets: { view: true, edit: true },
        depreciation: { view: true, edit: true, post: true },
        disposal: { view: true, edit: true, post: true },
        appreciation: { view: true, edit: true, post: true },
        reports: { view: true }
      },
      reports: {
        purchaseReports: true, saleReports: true, inventoryReports: true,
        financialReports: true, profitReports: true, intelligenceReports: true,
        jobReports: true, productionReports: true
      },
      settings: { financialYear: { view: true }, documentSettings: { view: true }, printFormats: { view: true } }
    })
  },
  {
    name: 'Sales',
    description: 'Access to sales and customer modules',
    isSystemRole: false,
    permissions: buildPermissions({
      masterData: { customers: { view: true, edit: true }, items: { view: true } },
      transactions: {
        quotation: { view: true, edit: true }, saleOrder: { view: true, edit: true },
        deliveryChallan: { view: true, edit: true },
        saleInvoice: { view: true, edit: true }, saleReturn: { view: true, edit: true },
        nonTaxSaleInvoice: { view: true, edit: true }, nonTaxSaleReturn: { view: true, edit: true },
        cashReceipt: { view: true }, bankReceipt: { view: true }
      },
      reports: { saleReports: true }
    })
  },
  {
    name: 'Store Keeper',
    description: 'Access to inventory and store modules',
    isSystemRole: false,
    permissions: buildPermissions({
      masterData: {
        vendors: { view: true }, items: { view: true, edit: true }, locations: { view: true, edit: true }
      },
      transactions: {
        purchaseOrder: { view: true }, grn: { view: true, edit: true },
        addStock: { view: true, edit: true }, reduceStock: { view: true, edit: true },
        stockTransfer: { view: true, edit: true }
      },
      store: {
        purchaseRequisition: { view: true, edit: true, delete: true },
        inwardGatePass: { view: true, edit: true, delete: true },
        outwardGatePass: { view: true, edit: true, delete: true },
        billOfMaterials: { view: true, edit: true, delete: true },
        productionOrder: { view: true, edit: true, delete: true },
        materialIssue: { view: true, edit: true, delete: true },
        productionReceipt: { view: true, edit: true, delete: true }
      },
      reports: { inventoryReports: true }
    })
  },
  {
    name: 'Cashier',
    description: 'POS counter sale access only',
    isSystemRole: false,
    permissions: buildPermissions({
      masterData: { customers: { view: true }, items: { view: true } },
      transactions: { posCounterSale: { view: true, edit: true } },
      reports: { posReports: true }
    })
  },
  {
    name: 'Viewer',
    description: 'Read-only access to all modules',
    isSystemRole: false,
    permissions: (() => {
      const p = {};
      permissionCategories.forEach(cat => {
        p[cat.key] = {};
        cat.modules.forEach(mod => {
          if (cat.actions[0] === 'toggle') { p[cat.key][mod.key] = true; }
          else { p[cat.key][mod.key] = { view: true }; cat.actions.filter(a => a !== 'view').forEach(a => { p[cat.key][mod.key][a] = false; }); }
        });
      });
      return p;
    })()
  }
];

const UsersRoles = () => {
  const { activeCompany, currentUser, companyUser, isBranchModuleEnabled, branches, fetchBranches, isModuleEnabled } = useAuth();
  const { canEdit } = usePagePermissions('settings', 'usersRoles');
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  // Modal states
  const [showUserModal, setShowUserModal] = useState(false);
  useEscapeClose(showUserModal, () => setShowUserModal(false));
  const [showRoleModal, setShowRoleModal] = useState(false);
  useEscapeClose(showRoleModal, () => setShowRoleModal(false));
  const [editingUser, setEditingUser] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [roleEditorTab, setRoleEditorTab] = useState('masterData');

  // Form states
  const [userForm, setUserForm] = useState({
    displayName: '',
    email: '',
    password: '',
    phone: '',
    roleId: '',
    isActive: true,
    assignedBranches: []
  });
  const [showPassword, setShowPassword] = useState(false);

  const [roleForm, setRoleForm] = useState({
    name: '',
    description: '',
    permissions: buildDefaultPermissions()
  });

  // Seed default roles if none exist (uses deterministic IDs to prevent duplicates)
  const seedDefaultRoles = useCallback(async () => {
    if (!activeCompany?.id) return;

    try {
      for (const role of defaultRoles) {
        // Use role name as deterministic doc ID to prevent duplicates on double-call
        const roleId = role.name.toLowerCase().replace(/\s+/g, '_');
        const roleRef = doc(db, 'companies', activeCompany.id, 'roles', roleId);
        await setDoc(roleRef, {
          ...role,
          createdAt: new Date().toISOString(),
          createdBy: currentUser?.uid
        }, { merge: true });
      }

      return true;
    } catch (error) {
      console.error('Error seeding default roles:', error);
      return false;
    }
  }, [activeCompany, currentUser]);

  const loadData = useCallback(async () => {
    if (!activeCompany?.id) return;

    try {
      setLoading(true);

      // Load users
      const usersRef = collection(db, 'companies', activeCompany.id, 'users');
      const usersSnapshot = await getDocs(query(usersRef, orderBy('displayName')));
      const usersList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersList);

      // Load roles
      const rolesRef = collection(db, 'companies', activeCompany.id, 'roles');
      let rolesSnapshot = await getDocs(query(rolesRef, orderBy('name')));

      // If no roles exist, seed default roles
      if (rolesSnapshot.empty) {
        const seeded = await seedDefaultRoles();
        if (seeded) {
          // Reload roles after seeding
          rolesSnapshot = await getDocs(query(rolesRef, orderBy('name')));
        }
      }

      const rolesList = rolesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRoles(rolesList);

      // Load branches if multi-branch module is enabled
      if (isBranchModuleEnabled()) {
        await fetchBranches(activeCompany.id);
      }

    } catch (error) {
      console.error('Error loading data:', error);
      setAlert({ show: true, type: 'error', message: 'Failed to load data' });
    } finally {
      setLoading(false);
    }
  }, [activeCompany, seedDefaultRoles, isBranchModuleEnabled, fetchBranches]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleName = (roleId) => {
    if (roleId === 'admin') return 'Administrator';
    const role = roles.find(r => r.id === roleId);
    return role?.name || 'Unknown Role';
  };

  // User Modal Functions
  const openAddUserModal = () => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    setEditingUser(null);
    setUserForm({
      displayName: '',
      email: '',
      password: '',
      phone: '',
      roleId: roles.length > 0 ? roles[0].id : '',
      isActive: true,
      assignedBranches: []
    });
    setShowPassword(false);
    setShowUserModal(true);
  };

  const openEditUserModal = (user) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    setEditingUser(user);
    setUserForm({
      displayName: user.displayName || '',
      email: user.email || '',
      password: '', // Password not shown for editing
      phone: user.phone || '',
      roleId: user.roleId || '',
      isActive: user.isActive !== false,
      assignedBranches: user.assignedBranches || []
    });
    setShowUserModal(true);
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }

    if (!userForm.displayName.trim()) {
      setAlert({ show: true, type: 'error', message: 'Display name is required' });
      return;
    }

    if (!userForm.email.trim()) {
      setAlert({ show: true, type: 'error', message: 'Email is required' });
      return;
    }

    // Password required only for new users
    if (!editingUser && !userForm.password.trim()) {
      setAlert({ show: true, type: 'error', message: 'Password is required for new users' });
      return;
    }

    if (!editingUser && userForm.password.length < 6) {
      setAlert({ show: true, type: 'error', message: 'Password must be at least 6 characters' });
      return;
    }

    // Package limit guard (creation only). Editing existing users is always allowed.
    const maxUsers = Number(activeCompany?.maxUsers) || 0;
    if (!editingUser && maxUsers > 0 && users.length >= maxUsers) {
      setAlert({
        show: true,
        type: 'error',
        message: `Your package allows up to ${maxUsers} user${maxUsers === 1 ? '' : 's'}. Contact your administrator to upgrade.`
      });
      return;
    }

    setSaving(true);
    try {
      if (editingUser) {
        // Update existing user
        const userRef = doc(db, 'companies', activeCompany.id, 'users', editingUser.id);
        await updateDoc(userRef, {
          displayName: userForm.displayName,
          phone: userForm.phone,
          roleId: userForm.roleId,
          isActive: userForm.isActive,
          assignedBranches: userForm.assignedBranches,
          updatedAt: new Date().toISOString(),
          updatedBy: currentUser?.uid
        });
        // Also update the global user profile (users/{uid}) so the top-right name,
        // avatar, and any other place reading userProfile reflect the change.
        try {
          await updateDoc(doc(db, 'users', editingUser.id), {
            displayName: userForm.displayName,
            phone: userForm.phone || '',
            updatedAt: new Date().toISOString()
          });
        } catch (globalErr) {
          console.warn('Could not sync global user profile:', globalErr);
        }
        setAlert({ show: true, type: 'success', message: 'User updated successfully' });
      } else {
        // SEC-02 (2026-09-30): the login, profile and membership are now
        // created on the server (Cloud Function createCompanyUser). Same saved
        // fields as before; the admin also stays logged in (previously the
        // browser switched to the new user's session).
        const createCompanyUser = httpsCallable(functions, 'createCompanyUser');
        await createCompanyUser({
          companyId: activeCompany.id,
          email: userForm.email.toLowerCase(),
          password: userForm.password,
          displayName: userForm.displayName,
          phone: userForm.phone || '',
          roleId: userForm.roleId,
          assignedBranches: userForm.assignedBranches
        });

        setAlert({
          show: true,
          type: 'success',
          message: `User created successfully! Credentials: ${userForm.email} / ${userForm.password}`
        });
      }

      setShowUserModal(false);
      loadData();
    } catch (error) {
      console.error('Error saving user:', error);
      let errorMessage = 'Failed to save user';
      if (error.code === 'auth/email-already-in-use' || error.code === 'functions/already-exists') {
        errorMessage = 'A user with this email already exists';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak';
      } else if (error.code === 'functions/invalid-argument' || error.code === 'functions/permission-denied') {
        errorMessage = error.message || errorMessage;
      }
      setAlert({ show: true, type: 'error', message: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  // ─── Set Password (company owner / admin) ─────────────────────────────────
  // Sets a NEW password for a user (server function setCompanyUserPassword).
  // Current passwords can never be seen. Owner's / own / other admins' (unless
  // owner) rows have no button; the server re-checks everything.
  const isCompanyOwner = !!(activeCompany?.ownerId && currentUser?.uid && activeCompany.ownerId === currentUser.uid);
  const canSetPasswords = isCompanyOwner || companyUser?.role === 'admin';
  const canSetPasswordFor = (u) => canSetPasswords && u.id !== currentUser?.uid
    && u.id !== activeCompany?.ownerId && (u.role !== 'admin' || isCompanyOwner);
  const [pwUser, setPwUser] = useState(null);
  const [pwForm, setPwForm] = useState({ p1: '', p2: '' });
  const [pwShow, setPwShow] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState('');
  const openSetPassword = (u) => { setPwUser(u); setPwForm({ p1: '', p2: '' }); setPwShow(false); setPwError(''); };
  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (!pwUser) return;
    if (pwForm.p1.length < 6) { setPwError('Password must be at least 6 characters.'); return; }
    if (pwForm.p1 !== pwForm.p2) { setPwError('The two passwords do not match.'); return; }
    setPwSaving(true);
    setPwError('');
    try {
      const fn = httpsCallable(functions, 'setCompanyUserPassword');
      await fn({ companyId: activeCompany.id, userId: pwUser.id, newPassword: pwForm.p1 });
      setAlert({ show: true, type: 'success', message: `New password set for ${pwUser.displayName || pwUser.email}. They have been signed out on all devices — share the new password with them.` });
      setPwUser(null);
    } catch (err) {
      setPwError(err?.message || 'Could not set the password.');
    } finally {
      setPwSaving(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (user.id === currentUser?.uid) {
      setAlert({ show: true, type: 'error', message: 'You cannot delete your own account' });
      return;
    }

    if (!window.confirm(`Are you sure you want to remove ${user.displayName} from this company?`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'companies', activeCompany.id, 'users', user.id));
      setAlert({ show: true, type: 'success', message: 'User removed successfully' });
      loadData();
    } catch (error) {
      console.error('Error deleting user:', error);
      setAlert({ show: true, type: 'error', message: 'Failed to remove user' });
    }
  };

  // Role Modal Functions
  const openAddRoleModal = () => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    setEditingRole(null);
    setRoleForm({
      name: '',
      description: '',
      permissions: buildDefaultPermissions()
    });
    setRoleEditorTab('masterData');
    setShowRoleModal(true);
  };

  const openEditRoleModal = (role) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    setEditingRole(role);
    setRoleForm({
      name: role.name || '',
      description: role.description || '',
      permissions: migratePermissions(role.permissions)
    });
    setRoleEditorTab('masterData');
    setShowRoleModal(true);
  };

  // ─── Report-wise rights (utils/reportRights.js) ───────────────────────────
  // Inside each report head, single reports can be switched off. Only the OFF
  // ones are stored, so untouched roles stay exactly as they were.
  const [openReportHeads, setOpenReportHeads] = useState({});
  const headReports = (headKey) => reportsOfHead(headKey).filter(m => !isModuleEnabled || isModuleEnabled(m.id));
  const headState = (perms, headKey) => {
    const list = headReports(headKey);
    const items = perms[REPORT_ITEMS_KEY] || {};
    const head = perms.reports?.[headKey] === true;
    const on = head ? list.filter(m => items[m.id] !== false).length : 0;
    return { list, head, on, partial: head && on < list.length };
  };
  const dropHeadItems = (perms, headKey) => {
    if (!perms[REPORT_ITEMS_KEY]) return;
    reportsOfHead(headKey).forEach(m => { delete perms[REPORT_ITEMS_KEY][m.id]; });
    if (Object.keys(perms[REPORT_ITEMS_KEY]).length === 0) delete perms[REPORT_ITEMS_KEY];
  };
  // Head tick: all on ↔ all off (a partly-on head becomes all on).
  const toggleReportHead = (headKey) => {
    setRoleForm(prev => {
      const p = JSON.parse(JSON.stringify(prev.permissions));
      const st = headState(p, headKey);
      p.reports[headKey] = !(st.head && !st.partial);
      dropHeadItems(p, headKey);
      return { ...prev, permissions: p };
    });
  };
  // One report's tick. Ticking one under a head that was off opens only that one;
  // unticking the last one switches the head off.
  const toggleReportItem = (headKey, reportId) => {
    setRoleForm(prev => {
      const p = JSON.parse(JSON.stringify(prev.permissions));
      const st = headState(p, headKey);
      const items = { ...(p[REPORT_ITEMS_KEY] || {}) };
      const isOn = st.head && items[reportId] !== false;
      if (!isOn) {
        if (!st.head) {
          p.reports[headKey] = true;
          st.list.forEach(m => { if (m.id !== reportId) items[m.id] = false; });
        }
        delete items[reportId];
      } else {
        items[reportId] = false;
        if (st.list.every(m => items[m.id] === false)) {
          p.reports[headKey] = false;
          reportsOfHead(headKey).forEach(m => { delete items[m.id]; });
        }
      }
      if (Object.keys(items).length) p[REPORT_ITEMS_KEY] = items; else delete p[REPORT_ITEMS_KEY];
      return { ...prev, permissions: p };
    });
  };

  const handlePermissionChange = (categoryKey, moduleKey, action) => {
    setRoleForm(prev => {
      const newPerms = JSON.parse(JSON.stringify(prev.permissions));
      if (action === 'toggle') {
        newPerms[categoryKey][moduleKey] = !newPerms[categoryKey][moduleKey];
      } else {
        if (!newPerms[categoryKey][moduleKey]) newPerms[categoryKey][moduleKey] = {};
        const mp = newPerms[categoryKey][moduleKey];
        const cat = permissionCategories.find(c => c.key === categoryKey);
        const mod = cat && cat.modules.find(m => m.key === moduleKey);
        if (cat && hasAddSplit(cat, mod) && (action === 'add' || action === 'edit')) {
          if (action === 'add') {
            if (mp.edit === true) { mp.edit = false; delete mp.addOnly; }    // Add off → no adding or changing
            else { mp.edit = true; mp.addOnly = true; }                      // Add on → add only
          } else if (mp.edit === true && mp.addOnly !== true) {
            mp.addOnly = true;                                               // Edit off → Add stays
          } else {
            mp.edit = true; delete mp.addOnly;                               // Edit on (Add comes with it)
          }
        } else {
          mp[action] = !mp[action];
        }
      }
      return { ...prev, permissions: newPerms };
    });
  };

  const handleToggleAllModule = (categoryKey, moduleKey) => {
    const category = permissionCategories.find(c => c.key === categoryKey);
    if (!category || category.actions[0] === 'toggle') return;
    setRoleForm(prev => {
      const newPerms = JSON.parse(JSON.stringify(prev.permissions));
      const current = newPerms[categoryKey][moduleKey] || {};
      const mod = category.modules.find(m => m.key === moduleKey);
      const allOn = actionsOf(category, mod).every(a => permOn(category, mod, current, a));
      newPerms[categoryKey][moduleKey] = {};
      storedActionsOf(category, mod).forEach(a => { newPerms[categoryKey][moduleKey][a] = !allOn; });
      return { ...prev, permissions: newPerms };
    });
  };

  const handleToggleAllCategory = (categoryKey) => {
    const category = permissionCategories.find(c => c.key === categoryKey);
    if (!category) return;
    setRoleForm(prev => {
      const newPerms = JSON.parse(JSON.stringify(prev.permissions));
      const allOn = category.modules.every(mod => {
        if (category.actions[0] === 'toggle') return newPerms[categoryKey][mod.key] === true;
        return actionsOf(category, mod).every(a => permOn(category, mod, newPerms[categoryKey][mod.key], a));
      }) && !(categoryKey === 'reports' && newPerms[REPORT_ITEMS_KEY] && Object.keys(newPerms[REPORT_ITEMS_KEY]).length);
      // Reports: Select / Deselect All also clears the single-report offs.
      if (categoryKey === 'reports') delete newPerms[REPORT_ITEMS_KEY];
      category.modules.forEach(mod => {
        if (category.actions[0] === 'toggle') { newPerms[categoryKey][mod.key] = !allOn; }
        else {
          newPerms[categoryKey][mod.key] = {};
          storedActionsOf(category, mod).forEach(a => { newPerms[categoryKey][mod.key][a] = !allOn; });
        }
      });
      return { ...prev, permissions: newPerms };
    });
  };

  const handleSaveRole = async (e) => {
    e.preventDefault();
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }

    if (!roleForm.name.trim()) {
      setAlert({ show: true, type: 'error', message: 'Role name is required' });
      return;
    }

    setSaving(true);
    try {
      const roleData = {
        name: roleForm.name,
        description: roleForm.description,
        permissions: roleForm.permissions,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser?.uid
      };

      if (editingRole) {
        await updateDoc(doc(db, 'companies', activeCompany.id, 'roles', editingRole.id), roleData);
        setAlert({ show: true, type: 'success', message: 'Role updated successfully' });
      } else {
        roleData.createdAt = new Date().toISOString();
        roleData.createdBy = currentUser?.uid;
        await addDoc(collection(db, 'companies', activeCompany.id, 'roles'), roleData);
        setAlert({ show: true, type: 'success', message: 'Role created successfully' });
      }

      setShowRoleModal(false);
      loadData();
    } catch (error) {
      console.error('Error saving role:', error);
      setAlert({ show: true, type: 'error', message: 'Failed to save role' });
    } finally {
      setSaving(false);
    }
  };

  // Fill in any missing permission keys on every role with `false` (default-deny).
  // Useful after adding new modules (e.g. non-tax invoices) or after the Firestore
  // rules deploy, so that roles written before the current permission schema are
  // brought up to date without overwriting any user's existing choices.
  const handleRepairRolePermissions = async () => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (!activeCompany?.id) return;
    if (!window.confirm(
      'Repair role permissions?\n\n' +
      'This scans every role in this company and fills in any missing permission ' +
      'entries with "off" (false). Existing choices are preserved.\n\n' +
      'Safe to run any time. Continue?'
    )) return;

    setSaving(true);
    try {
      let updated = 0;
      for (const role of roles) {
        const existing = role.permissions || {};
        const merged = buildDefaultPermissions();
        // Preserve any existing true values from the role's matrix
        permissionCategories.forEach(cat => {
          const existingCat = existing[cat.key];
          if (!existingCat) return;
          cat.modules.forEach(mod => {
            const existingMod = existingCat[mod.key];
            if (existingMod === undefined || existingMod === null) return;
            if (cat.actions[0] === 'toggle') {
              merged[cat.key][mod.key] = existingMod === true;
            } else {
              storedActionsOf(cat, mod).forEach(a => {
                if (existingMod[a] === true) merged[cat.key][mod.key][a] = true;
              });
              // Keep an "Add only" choice.
              if (hasAddSplit(cat, mod) && existingMod.addOnly === true && existingMod.edit === true) merged[cat.key][mod.key].addOnly = true;
            }
          });
        });

        // Report-wise offs live outside the matrix — keep them.
        if (existing[REPORT_ITEMS_KEY]) merged[REPORT_ITEMS_KEY] = existing[REPORT_ITEMS_KEY];
        // Only write if the permissions structure actually changed
        if (JSON.stringify(existing) !== JSON.stringify(merged)) {
          await updateDoc(doc(db, 'companies', activeCompany.id, 'roles', role.id), {
            permissions: merged,
            updatedAt: new Date().toISOString(),
            updatedBy: currentUser?.uid
          });
          updated++;
        }
      }
      setAlert({ show: true, type: 'success', message: `Repair complete. ${updated} role(s) updated.` });
      await loadData();
    } catch (err) {
      console.error('Error repairing role permissions:', err);
      setAlert({ show: true, type: 'error', message: 'Repair failed. See console.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async (role) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    // Check if it's a system role
    if (role.isSystemRole) {
      setAlert({
        show: true,
        type: 'error',
        message: 'Cannot delete system roles. They are required for the application to function properly.'
      });
      return;
    }

    // Check if any users are using this role
    const usersWithRole = users.filter(u => u.roleId === role.id);
    if (usersWithRole.length > 0) {
      setAlert({
        show: true,
        type: 'error',
        message: `Cannot delete role. ${usersWithRole.length} user(s) are assigned to this role.`
      });
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the role "${role.name}"?`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'companies', activeCompany.id, 'roles', role.id));
      setAlert({ show: true, type: 'success', message: 'Role deleted successfully' });
      loadData();
    } catch (error) {
      console.error('Error deleting role:', error);
      setAlert({ show: true, type: 'error', message: 'Failed to delete role' });
    }
  };

  return (
    <PageLayout pageTitle="Users & Roles">
      <Container>
        <PageHeader>
          <HeaderText>
            <PageTitle>Users & Roles</PageTitle>
            <PageDescription>
              Manage team members and their permissions in this company.
            </PageDescription>
          </HeaderText>
          {canEdit && (activeTab === 'users' ? (
            <AddButton onClick={openAddUserModal}>
              <FiPlus />
              Add User
            </AddButton>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <AddButton onClick={handleRepairRolePermissions} disabled={saving} style={{ background: colors.neutral[600] }} title="Fill missing permission entries with 'off' on every role. Preserves existing choices.">
                <FiTool />
                Repair Permissions
              </AddButton>
              <AddButton onClick={openAddRoleModal}>
                <FiPlus />
                Create Role
              </AddButton>
            </div>
          ))}
        </PageHeader>

        {alert.show && (
          <Alert $type={alert.type}>
            <FiAlertCircle />
            {alert.message}
          </Alert>
        )}

        <TabContainer>
          <Tab $active={activeTab === 'users'} onClick={() => setActiveTab('users')}>
            <FiUsers size={16} />
            Users ({users.length})
          </Tab>
          <Tab $active={activeTab === 'roles'} onClick={() => setActiveTab('roles')}>
            <FiShield size={16} />
            Roles ({roles.length})
          </Tab>
        </TabContainer>

        <ContentCard>
          {loading ? (
            <EmptyState>
              <EmptyText>Loading...</EmptyText>
            </EmptyState>
          ) : activeTab === 'users' ? (
            users.length === 0 ? (
              <EmptyState>
                <EmptyIcon><FiUsers /></EmptyIcon>
                <EmptyText>No Users Yet</EmptyText>
                <EmptySubtext>Add team members to collaborate on this company.</EmptySubtext>
              </EmptyState>
            ) : (
              <Table>
                <Thead>
                  <tr>
                    <Th>User</Th>
                    <Th>Role</Th>
                    <Th>Status</Th>
                    <Th>Last Login</Th>
                    <Th style={{ width: '100px' }}>Actions</Th>
                  </tr>
                </Thead>
                <Tbody>
                  {users.map(user => (
                    <Tr key={user.id}>
                      <Td>
                        <UserInfo>
                          <Avatar>{getInitials(user.displayName)}</Avatar>
                          <UserDetails>
                            <UserName>{user.displayName || 'Unknown'}</UserName>
                            <UserEmail>{user.email}</UserEmail>
                          </UserDetails>
                        </UserInfo>
                      </Td>
                      <Td>
                        <RoleBadge>
                          {user.role === 'admin' ? 'Administrator' : getRoleName(user.roleId)}
                        </RoleBadge>
                      </Td>
                      <Td>
                        <Badge $active={user.isActive !== false}>
                          {user.isActive !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </Td>
                      <Td>
                        {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString('en-PK') : 'Never'}
                      </Td>
                      <Td>
                        <ActionButtons>
                          {canEdit && (
                            <IconButton onClick={() => openEditUserModal(user)} title="Edit user">
                              <FiEdit2 size={16} />
                            </IconButton>
                          )}
                          {canSetPasswordFor(user) && (
                            <IconButton onClick={() => openSetPassword(user)} title="Set new password">
                              <FiKey size={16} />
                            </IconButton>
                          )}
                          {canEdit && user.id !== currentUser?.uid && user.role !== 'admin' && (
                            <IconButton $danger onClick={() => handleDeleteUser(user)} title="Remove user">
                              <FiTrash2 size={16} />
                            </IconButton>
                          )}
                        </ActionButtons>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )
          ) : (
            roles.length === 0 ? (
              <EmptyState>
                <EmptyIcon><FiShield /></EmptyIcon>
                <EmptyText>No Roles Created</EmptyText>
                <EmptySubtext>Create roles to manage permissions for different user types.</EmptySubtext>
              </EmptyState>
            ) : (
              <Table>
                <Thead>
                  <tr>
                    <Th>Role Name</Th>
                    <Th>Description</Th>
                    <Th>Users</Th>
                    <Th style={{ width: '100px' }}>Actions</Th>
                  </tr>
                </Thead>
                <Tbody>
                  {roles.map(role => (
                    <Tr key={role.id}>
                      <Td>
                        <strong>{role.name}</strong>
                        {role.isSystemRole && <SystemBadge>System</SystemBadge>}
                      </Td>
                      <Td>{role.description || '-'}</Td>
                      <Td>
                        <Badge $active>
                          {users.filter(u => u.roleId === role.id).length} users
                        </Badge>
                      </Td>
                      <Td>
                        <ActionButtons>
                          {canEdit && (
                            <IconButton onClick={() => openEditRoleModal(role)} title="Edit role">
                              <FiEdit2 size={16} />
                            </IconButton>
                          )}
                          {canEdit && !role.isSystemRole && (
                            <IconButton $danger onClick={() => handleDeleteRole(role)} title="Delete role">
                              <FiTrash2 size={16} />
                            </IconButton>
                          )}
                        </ActionButtons>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )
          )}
        </ContentCard>

        {/* User Modal */}
        {showUserModal && (
          <ModalOverlay>
            <ModalContent onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{editingUser ? 'Edit User' : 'Create User'}</ModalTitle>
                <CloseButton onClick={() => setShowUserModal(false)}>
                  <FiX />
                </CloseButton>
              </ModalHeader>
              <form onSubmit={handleSaveUser}>
                <ModalBody>
                  <FormGroup>
                    <Label>Display Name <span>*</span></Label>
                    <Input
                      type="text"
                      value={userForm.displayName}
                      onChange={(e) => setUserForm(prev => ({ ...prev, displayName: e.target.value }))}
                      placeholder="Full name"
                    />
                  </FormGroup>

                  <FormRow>
                    <FormGroup>
                      <Label>Email <span>*</span></Label>
                      <Input
                        type="email"
                        value={userForm.email}
                        onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="user@example.com"
                        disabled={!!editingUser}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Phone</Label>
                      <Input
                        type="tel"
                        value={userForm.phone}
                        onChange={(e) => setUserForm(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="+92-300-1234567"
                      />
                    </FormGroup>
                  </FormRow>

                  {!editingUser && (
                    <FormGroup>
                      <Label>Password <span>*</span></Label>
                      <PasswordInputWrapper>
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={userForm.password}
                          onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))}
                          placeholder="Min 6 characters"
                          style={{ paddingRight: '40px' }}
                        />
                        <PasswordToggle type="button" onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                        </PasswordToggle>
                      </PasswordInputWrapper>
                      <HelpText>Share these credentials with the user securely.</HelpText>
                    </FormGroup>
                  )}

                  <FormRow>
                    <FormGroup>
                      <Label>Role <span>*</span></Label>
                      <Select
                        value={userForm.roleId}
                        onChange={(e) => setUserForm(prev => ({ ...prev, roleId: e.target.value }))}
                      >
                        <option value="">Select Role</option>
                        {roles.map(role => (
                          <option key={role.id} value={role.id}>{role.name}</option>
                        ))}
                      </Select>
                    </FormGroup>

                    <FormGroup>
                      <Label>Status</Label>
                      <Select
                        value={userForm.isActive ? 'active' : 'inactive'}
                        onChange={(e) => setUserForm(prev => ({ ...prev, isActive: e.target.value === 'active' }))}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </Select>
                    </FormGroup>
                  </FormRow>

                  {isBranchModuleEnabled() && branches.length > 0 && (
                    <FormGroup>
                      <Label>Assigned Branches</Label>
                      <BranchCheckboxList>
                        {branches.map(branch => (
                          <BranchCheckboxItem key={branch.id}>
                            <input
                              type="checkbox"
                              checked={userForm.assignedBranches.includes(branch.id)}
                              onChange={(e) => {
                                setUserForm(prev => ({
                                  ...prev,
                                  assignedBranches: e.target.checked
                                    ? [...prev.assignedBranches, branch.id]
                                    : prev.assignedBranches.filter(id => id !== branch.id)
                                }));
                              }}
                            />
                            <span>{branch.branchName}</span>
                            {branch.branchCode && <BranchCode>({branch.branchCode})</BranchCode>}
                          </BranchCheckboxItem>
                        ))}
                      </BranchCheckboxList>
                      <HelpText>Select which branches this user can access. Admin users can see all branches regardless.</HelpText>
                    </FormGroup>
                  )}
                </ModalBody>
                <ModalFooter>
                  <CancelButton type="button" onClick={() => setShowUserModal(false)}>
                    Cancel
                  </CancelButton>
                  {canEdit && (
                    <SaveButton type="submit" disabled={saving}>
                      <FiSave />
                      {saving ? 'Saving...' : editingUser ? 'Update User' : 'Create User'}
                    </SaveButton>
                  )}
                </ModalFooter>
              </form>
            </ModalContent>
          </ModalOverlay>
        )}

        {/* Set Password Modal */}
        {pwUser && (
          <ModalOverlay>
            <ModalContent onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>Set New Password</ModalTitle>
                <CloseButton onClick={() => setPwUser(null)}><FiX /></CloseButton>
              </ModalHeader>
              <form onSubmit={handleSetPassword}>
                <ModalBody>
                  <HelpText style={{ display: 'block', marginBottom: 16, fontSize: 13, lineHeight: 1.5 }}>
                    <strong>{pwUser.displayName || pwUser.email}</strong> ({pwUser.email})<br />
                    The old password stops working at once and this user is signed out on all devices.
                    Current passwords can never be seen — share the new one with the user.
                  </HelpText>
                  <FormGroup>
                    <Label>New Password <span>*</span></Label>
                    <PasswordInputWrapper>
                      <Input type={pwShow ? 'text' : 'password'} value={pwForm.p1} autoComplete="new-password" aria-label="New password"
                        onChange={(e) => setPwForm(p => ({ ...p, p1: e.target.value }))} placeholder="Min 6 characters" style={{ paddingRight: '40px' }} />
                      <PasswordToggle type="button" onClick={() => setPwShow(!pwShow)}>
                        {pwShow ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                      </PasswordToggle>
                    </PasswordInputWrapper>
                  </FormGroup>
                  <FormGroup>
                    <Label>Confirm Password <span>*</span></Label>
                    <Input type={pwShow ? 'text' : 'password'} value={pwForm.p2} autoComplete="new-password" aria-label="Confirm password"
                      onChange={(e) => setPwForm(p => ({ ...p, p2: e.target.value }))} />
                  </FormGroup>
                  {pwError && <HelpText style={{ display: 'block', color: '#DC3545', fontSize: 13 }}>{pwError}</HelpText>}
                </ModalBody>
                <ModalFooter>
                  <CancelButton type="button" onClick={() => setPwUser(null)}>Cancel</CancelButton>
                  <SaveButton type="submit" disabled={pwSaving}><FiKey />{pwSaving ? 'Saving...' : 'Set Password'}</SaveButton>
                </ModalFooter>
              </form>
            </ModalContent>
          </ModalOverlay>
        )}

        {/* Role Modal */}
        {showRoleModal && (
          <ModalOverlay>
            <ModalContent $wide onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{editingRole ? 'Edit Role' : 'Create Role'}</ModalTitle>
                <CloseButton onClick={() => setShowRoleModal(false)}>
                  <FiX />
                </CloseButton>
              </ModalHeader>
              <form onSubmit={handleSaveRole}>
                <ModalBody>
                  <FormRow>
                    <FormGroup>
                      <Label>Role Name <span>*</span></Label>
                      <Input
                        type="text"
                        value={roleForm.name}
                        onChange={(e) => setRoleForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., Sales Manager"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Description</Label>
                      <Input
                        type="text"
                        value={roleForm.description}
                        onChange={(e) => setRoleForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Brief description of this role"
                      />
                    </FormGroup>
                  </FormRow>

                  <TabContainer style={{ marginTop: 16, marginBottom: 0, flexWrap: 'wrap' }}>
                    {permissionCategories.map(cat => (
                      <Tab key={cat.key} $active={roleEditorTab === cat.key} onClick={(e) => { e.preventDefault(); setRoleEditorTab(cat.key); }}>
                        {cat.name}
                      </Tab>
                    ))}
                  </TabContainer>

                  {(() => {
                    const category = permissionCategories.find(c => c.key === roleEditorTab);
                    if (!category) return null;
                    const catPerms = roleForm.permissions[category.key] || {};
                    const isToggle = category.actions[0] === 'toggle';
                    const actions = columnsOf(category);
                    const gridCols = isToggle ? '1fr 80px' : `200px repeat(${actions.length}, 1fr)`;

                    return (
                      <FormGroup style={{ marginTop: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <Label style={{ margin: 0 }}>{category.name} Permissions</Label>
                          <span
                            style={{ fontSize: 12, color: colors.primary.main, cursor: 'pointer', fontWeight: 500 }}
                            onClick={() => handleToggleAllCategory(category.key)}
                          >
                            Select / Deselect All
                          </span>
                        </div>
                        <PermissionsGrid>
                          <PermissionHeader style={{ gridTemplateColumns: gridCols }}>
                            <PermissionCell>{isToggle ? 'Report Group' : 'Module'}</PermissionCell>
                            {isToggle ? (
                              <PermissionCell $center>Allow</PermissionCell>
                            ) : (
                              actions.map(a => (
                                <PermissionCell key={a} $center>{a.charAt(0).toUpperCase() + a.slice(1)}</PermissionCell>
                              ))
                            )}
                          </PermissionHeader>
                          {category.key === 'reports' ? category.modules.map(mod => {
                            // Report head row with ▸ — opens the head's reports, each with its own tick.
                            const st = headState(roleForm.permissions, mod.key);
                            const open = !!openReportHeads[mod.key];
                            const items = roleForm.permissions[REPORT_ITEMS_KEY] || {};
                            return (
                              <React.Fragment key={mod.key}>
                                <PermissionRow style={{ gridTemplateColumns: gridCols }}>
                                  <PermissionCell>
                                    {st.list.length > 0 ? (
                                      <ModuleName
                                        role="button" aria-label={`Show ${mod.name}`} aria-expanded={open}
                                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                                        onClick={() => setOpenReportHeads(o => ({ ...o, [mod.key]: !o[mod.key] }))}
                                      >
                                        {open ? <FiChevronDown /> : <FiChevronRight />}
                                        {mod.name}
                                        <span style={{ fontSize: 12, fontWeight: 400, color: st.partial ? colors.warning?.dark || '#b45309' : colors.text.secondary }}>
                                          ({st.on}/{st.list.length})
                                        </span>
                                      </ModuleName>
                                    ) : (
                                      <ModuleName style={{ cursor: 'default' }}>{mod.name}</ModuleName>
                                    )}
                                  </PermissionCell>
                                  <PermissionCell $center>
                                    <Checkbox
                                      type="checkbox"
                                      aria-label={`Allow ${mod.name}`}
                                      checked={st.head}
                                      ref={el => { if (el) el.indeterminate = st.partial; }}
                                      onChange={() => toggleReportHead(mod.key)}
                                    />
                                  </PermissionCell>
                                </PermissionRow>
                                {open && st.list.map(rep => (
                                  <PermissionRow key={rep.id} style={{ gridTemplateColumns: gridCols, background: colors.neutral?.[50] || '#fafafa' }}>
                                    <PermissionCell style={{ paddingLeft: 44 }}>
                                      <span style={{ fontSize: 13, color: colors.text.primary }}>{rep.name}</span>
                                    </PermissionCell>
                                    <PermissionCell $center>
                                      <Checkbox
                                        type="checkbox"
                                        aria-label={`Allow report ${rep.name}`}
                                        checked={st.head && items[rep.id] !== false}
                                        onChange={() => toggleReportItem(mod.key, rep.id)}
                                      />
                                    </PermissionCell>
                                  </PermissionRow>
                                ))}
                              </React.Fragment>
                            );
                          }) : category.modules.map(mod => (
                            <PermissionRow key={mod.key} style={{ gridTemplateColumns: gridCols }}>
                              <PermissionCell>
                                <ModuleName
                                  style={{ cursor: isToggle ? 'default' : 'pointer' }}
                                  onClick={() => !isToggle && handleToggleAllModule(category.key, mod.key)}
                                >
                                  {mod.name}
                                </ModuleName>
                              </PermissionCell>
                              {isToggle ? (
                                <PermissionCell $center>
                                  <Checkbox
                                    type="checkbox"
                                    checked={catPerms[mod.key] === true}
                                    onChange={() => handlePermissionChange(category.key, mod.key, 'toggle')}
                                  />
                                </PermissionCell>
                              ) : (
                                actions.map(action => (
                                  <PermissionCell key={action} $center>
                                    {actionsOf(category, mod).includes(action) && (
                                      <Checkbox
                                        type="checkbox"
                                        checked={permOn(category, mod, catPerms[mod.key], action)}
                                        onChange={() => handlePermissionChange(category.key, mod.key, action)}
                                      />
                                    )}
                                  </PermissionCell>
                                ))
                              )}
                            </PermissionRow>
                          ))}
                        </PermissionsGrid>
                      </FormGroup>
                    );
                  })()}
                </ModalBody>
                <ModalFooter>
                  <CancelButton type="button" onClick={() => setShowRoleModal(false)}>
                    Cancel
                  </CancelButton>
                  {canEdit && (
                    <SaveButton type="submit" disabled={saving}>
                      <FiSave />
                      {saving ? 'Saving...' : editingRole ? 'Update Role' : 'Create Role'}
                    </SaveButton>
                  )}
                </ModalFooter>
              </form>
            </ModalContent>
          </ModalOverlay>
        )}
      </Container>
    </PageLayout>
  );
};

export default UsersRoles;
